import { usePostHog, PostHog } from "@posthog/react";
import { useCallback, useEffect, useRef } from "react";

let posthogInstance: PostHog | null = null;

export const setPostHogInstance = (instance: PostHog) => {
  posthogInstance = instance;
};

export const PostHogEvents = {
  // Messaging
  TEEN_MESSAGE_RECEIVED: "teen_message_received",
  MENTOR_MESSAGE_SENT: "mentor_message_sent",
  OPENED_CHAT: "opened_chat",

  // Calls / Sessions
  CALL_STARTED: "call_started",
  CALL_ENDED: "call_ended",
  SESSION_SCHEDULED: "session_scheduled",

  // Suggested Messages
  SUGGESTED_MESSAGE_INSERTED: "suggested_message_inserted",
  SUGGESTED_MESSAGE_SENT: "suggested_message_sent",
  SUGGESTED_MESSAGE_EDITED: "suggested_message_edited",

  // AI Copilot
  COPILOT_CHAT_STARTED: "copilot_chat_started",
  COPILOT_RESPONSE_INSERTED: "copilot_response_inserted",
  COPILOT_RESPONSE_ADDED_TO_NOTE: "copilot_response_added_to_note",

  // Documents
  DOCUMENT_CREATED: "document_created",
  DOCUMENT_EDITED: "document_edited",

  // Todos
  TODO_VIEWED: "todo_viewed",
  TODO_CLICKED: "todo_clicked",

  // Navigation
  PAGE_HOME: "page_home",
  PAGE_CHAT: "page_chat",
  PAGE_UPCOMING: "page_upcoming",
  PAGE_SETTINGS: "page_settings",
  PAGE_NEW_TEENS: "page_new_teens",

  // Hub - Publish/Share
  HUB_MOMENT_SHARED: "mentor_hub_moment_shared",
  HUB_NOTE_SHARED: "mentor_hub_note_shared",

  // Hub - Comments
  HUB_MOMENT_COMMENT_SENT: "mentor_hub_moment_comment_sent",
  HUB_NOTE_COMMENT_SENT: "mentor_hub_note_comment_sent",

  // Hub - Reactions
  HUB_MOMENT_REACTED: "mentor_hub_moment_reacted",
  HUB_NOTE_REACTED: "mentor_hub_note_reacted",

  // Hub - Read State
  HUB_MOMENT_MARKED_READ: "mentor_hub_moment_marked_read",
  HUB_MOMENT_MARKED_UNREAD: "mentor_hub_moment_marked_unread",
  HUB_NOTE_MARKED_READ: "mentor_hub_note_marked_read",
  HUB_NOTE_MARKED_UNREAD: "mentor_hub_note_marked_unread",

  // Hub - Open/View
  HUB_MOMENT_OPENED: "mentor_hub_moment_opened",
  HUB_NOTE_OPENED: "mentor_hub_note_opened",

  // Magic Moment - Day 1
  DAY1_BANNER_VIEW: "mentor_chat_day1_banner_view",
  DAY1_BANNER_TOGGLE: "mentor_chat_day1_banner_toggle",
  DAY1_TIP_CLICK: "mentor_chat_day1_tip_click",
  DAY1_BANNER_EXPIRE: "mentor_chat_day1_banner_expire",

  // Magic Moment - Day 2
  DAY2_BADGE_VIEW: "mentor_chat_day2_badge_view",
  DAY2_MODAL_VIEW: "mentor_chat_day2_modal_view",
  DAY2_MODAL_DISMISS: "mentor_chat_day2_modal_dismiss",
  DAY2_BANNER_VIEW: "mentor_chat_day2_banner_view",
  DAY2_BANNER_TOGGLE: "mentor_chat_day2_banner_toggle",
  DAY2_ACTION_CLICK: "mentor_chat_day2_action_click",
  DAY2_MAGIC_MOMENT_VIEW: "mentor_chat_day2_magic_moment_view",
  DAY2_TIPS_VIEW: "mentor_chat_day2_tips_view",
  DAY2_TIPS_BACK: "mentor_chat_day2_tips_back",
  DAY2_MAGIC_MOMENT_BACK: "mentor_chat_day2_magic_moment_back",
  DAY2_BANNER_EXPIRE: "mentor_chat_day2_banner_expire",
  DAY2_BANNER_DISMISS: "mentor_chat_day2_banner_dismiss",
  DAY1_BANNER_DISMISS: "mentor_chat_day1_banner_dismiss",

  // Ops Chat Dashboard
  OPS_CHAT_DASHBOARD_VIEW: "ops_chat_dashboard_view",
  OPS_CHAT_SEGMENT_TOGGLE: "ops_chat_segment_toggle",
  OPS_CHAT_CATEGORY_EXPAND: "ops_chat_category_expand",
  OPS_CHAT_THREAD_OPEN: "ops_chat_thread_open",
  OPS_CHAT_MESSAGE_SEND: "ops_chat_message_send",
  OPS_CHAT_NEW_CHAT_OPEN: "ops_chat_new_chat_open",
  OPS_CHAT_NEW_CHAT_DROPDOWN_VIEW: "ops_chat_new_chat_dropdown_view",
  OPS_CHAT_NEW_CHAT_DROPDOWN_FILTER: "ops_chat_new_chat_dropdown_filter",
  OPS_CHAT_NEW_CHAT_RECIPIENT_ADD: "ops_chat_new_chat_recipient_add",
  OPS_CHAT_NEW_CHAT_RECIPIENT_REMOVE: "ops_chat_new_chat_recipient_remove",
  OPS_CHAT_NEW_CHAT_HISTORY_RENDER: "ops_chat_new_chat_history_render",
  OPS_CHAT_NEW_CHAT_DISMISS: "ops_chat_new_chat_dismiss",
  OPS_CHAT_NEW_CHAT_SEND: "ops_chat_new_chat_send",
} as const;

export type PostHogEventName = (typeof PostHogEvents)[keyof typeof PostHogEvents];

export interface OpenedChatMetadata {
  teen_id?: string;
  teen_name?: string;
  channel_url?: string;
}

export interface CallMetadata {
  teen_id?: string;
  call_id?: string;
  room_id?: string;
}

export interface SessionMetadata {
  teen_id?: string;
  mentorship_id?: string;
  scheduled_time?: string;
}

export interface SuggestedMessageMetadata {
  suggestion_type?: "default" | "specific" | "priority";
  approach?: string;
  text_length?: number;
  was_edited?: boolean;
}

export interface CopilotMetadata {
  mentorship_id?: string;
  response_length?: number;
}

export interface DocumentMetadata {
  mentorship_id?: string;
  has_reminder?: boolean;
  note_id?: string;
}

export interface TodoMetadata {
  notification_type?: string;
  notification_id?: string;
  teen_name?: string;
}

export function useTrackEvent() {
  const posthog = usePostHog();

  // Store instance for non-hook usage (like Sendbird handlers)
  if (posthog && !posthogInstance) {
    posthogInstance = posthog;
  }

  const track = useCallback(
    (event: PostHogEventName, properties?: Record<string, unknown>) => {
      if (!posthog) return;
      posthog.capture(event, properties);
    },
    [posthog],
  );

  return { track };
}

export function useTrackPage(event: PostHogEventName, properties?: Record<string, unknown>) {
  const { track } = useTrackEvent();
  const tracked = useRef(false);

  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    track(event, properties);
  }, []);
}

export const trackEvent = (
  event: PostHogEventName,
  properties?: Record<string, unknown>,
) => {
  if (posthogInstance) {
    posthogInstance.capture(event, properties);
  } else if (window.posthog) {
    window.posthog.capture(event, properties);
  } else {
    console.warn(`📊 [PostHog] Event not sent - PostHog not available`);
  }
};

export function useSetPersonProperties(properties: Record<string, unknown> | undefined) {
  const posthog = usePostHog();

  useEffect(() => {
    if (properties && posthog) {
      posthog.people.set(properties);
    }
  }, [properties, posthog]);
}

export function useTrackMentorActiveTeens(activeTeens: number | undefined) {
  const posthog = usePostHog();

  useEffect(() => {
    if (activeTeens !== undefined && posthog) {
      posthog.people.set({ active_teens: activeTeens });
    }
  }, [activeTeens, posthog]);
}
